import cn from 'classnames';
import { format, isSameDay } from 'date-fns';
import * as React from 'react';

import { DateFormatsEnum } from '../../../../constants/date';
import useModal from '../../../../hooks/useModal';
import { UISizeEnum } from '../../../../types/shell';
import { getUTCDate } from '../../../../utils/date';
import { ActionButton, ControlButton, DropdownWithContent } from '../../../index';
import { DatePicker } from '../index';

import styles from './DateSinglePicker.module.scss';

interface IProps {
  className?: string;
  contentClassName?: string;
  value: string | null;
  formatRule: DateFormatsEnum;
  formatView?: DateFormatsEnum;
  onSelect?: (value: string | null) => void;
  onReset?: () => void;
  defaultLabel?: string;
  resetLabel?: string;
  submitLabel?: string;
  modalMode?: boolean;
  maxDate?: Date | null;
  minDate?: Date | null;
  readOnly?: boolean;
  closeModalOnReset?: boolean;
  defaultView?: boolean;
}

interface IControlProps {
  close: () => void;
}

const DateSinglePicker = React.forwardRef(
  (
    {
      className,
      contentClassName,
      defaultLabel,
      value,
      formatRule,
      formatView = DateFormatsEnum.ddMMMyyyy,
      onSelect,
      onReset,
      modalMode,
      resetLabel,
      submitLabel,
      maxDate,
      minDate,
      readOnly,
      closeModalOnReset,
      defaultView,
    }: IProps,
    ref: React.Ref<HTMLButtonElement>,
  ) => {
    const maxPickerDate = React.useMemo(() => (maxDate ? getUTCDate(maxDate.toISOString()) : maxDate), [maxDate]);
    const minPickerDate = React.useMemo(() => (minDate ? getUTCDate(minDate.toISOString()) : minDate), [minDate]);

    const [date, setDate] = React.useState<Date | null>(value ? getUTCDate(value) : null);

    const {
      Modal: MobileModal,
      openModal: openMobileModal,
      closeModal: closeMobileModal,
      isOpen: isMobileModalOpened,
    } = useModal({ resetStyles: true });

    const label = React.useMemo(() => {
      if (value) {
        const utcDate = getUTCDate(value);

        return `${utcDate ? format(utcDate, formatView) : 'Select date'}`;
      }
      return defaultLabel || 'Select date';
    }, [value, defaultLabel, formatView]);

    const handleSelect = React.useCallback(
      ({ close }: IControlProps) =>
        () => {
          onSelect?.(date ? format(date, formatRule) : null);
          close();
        },
      [date, formatRule, onSelect],
    );

    const handleOpen = React.useCallback(() => {
      if (value) {
        setDate(getUTCDate(value));
      }
    }, [value]);

    const handleClose = React.useCallback(() => {
      if (!date) {
        onSelect?.(null);
      }
    }, [date, onSelect]);

    const handleReset = React.useCallback(
      ({ close }: IControlProps) =>
        () => {
          if (closeModalOnReset) {
            close();
          }
          setDate(null);
          onSelect?.(null);
          onReset?.();
          close();
        },
      [onSelect, onReset, closeModalOnReset],
    );

    const renderControls = React.useCallback(
      ({ close }: IControlProps) => (
        <div className={styles.controls}>
          <ActionButton
            dataAction="reset_btn"
            className={styles.controlBtn}
            size={UISizeEnum.Small}
            outlined
            onClick={handleReset({ close })}
          >
            {resetLabel || 'Reset'}
          </ActionButton>
          <ActionButton
            dataAction="submit_btn"
            className={styles.controlBtn}
            size={UISizeEnum.Small}
            onClick={handleSelect({ close })}
          >
            {submitLabel || 'Select'}
          </ActionButton>
        </div>
      ),
      [handleReset, handleSelect, resetLabel, submitLabel],
    );

    const isSelectedDate = React.useCallback(
      (d: Date) => (date && isSameDay(date, d) ? styles.selected : null),
      [date],
    );

    const handleModalOpen = React.useCallback(() => {
      handleOpen();
      openMobileModal();
    }, [handleOpen, openMobileModal]);

    React.useEffect(() => {
      if (!modalMode && isMobileModalOpened) {
        closeMobileModal();
      }
    }, [modalMode, closeMobileModal, isMobileModalOpened]);

    const defaultLayout = React.useMemo(
      () => (
        <DatePicker
          selectsStart
          openToDate={date || undefined}
          startDate={date}
          onChange={(newStartDate) => {
            setDate(newStartDate as Date);
          }}
          maxDate={maxPickerDate}
          minDate={minPickerDate}
          inline
          disabledKeyboardNavigation
          dayClassName={isSelectedDate}
        />
      ),
      [date, isSelectedDate, maxPickerDate, minPickerDate],
    );

    const modalLayout = React.useMemo(
      () => (
        <MobileModal>
          {() => (
            <div className={cn(styles.picker, styles.modal)}>
              <div className={styles.calendarsPanel}>
                <DatePicker
                  selectsStart
                  openToDate={date || undefined}
                  startDate={date}
                  onChange={(newStartDate) => {
                    setDate(newStartDate as Date);
                  }}
                  maxDate={maxPickerDate}
                  minDate={minPickerDate}
                  inline
                  disabledKeyboardNavigation
                  dayClassName={isSelectedDate}
                />
              </div>
              {renderControls({ close: closeMobileModal })}
            </div>
          )}
        </MobileModal>
      ),
      [MobileModal, date, isSelectedDate, closeMobileModal, minPickerDate, maxPickerDate, renderControls],
    );

    return !modalMode ? (
      <DropdownWithContent
        canOpen={!readOnly}
        onOpen={handleOpen}
        onClose={handleClose}
        className={className}
        defaultView={defaultView}
        contentClassName={cn(styles.container, contentClassName)}
        label={({ isOpened }) => (
          <ControlButton
            ref={ref}
            label={label}
            dataAction="datapicker_trigger"
            isOpened={isOpened}
            readOnly={readOnly}
          />
        )}
      >
        {({ close }) => (
          <div className={cn(styles.picker, { [styles.defaultView]: defaultView })}>
            <div className={cn(styles.calendarsPanel)}>{defaultLayout}</div>
            {renderControls({ close })}
          </div>
        )}
      </DropdownWithContent>
    ) : (
      <React.Fragment>
        <ControlButton label={label} isOpened={isMobileModalOpened} onClick={handleModalOpen} />
        {modalLayout}
      </React.Fragment>
    );
  },
);

export default DateSinglePicker;
